import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { emailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { providerUsersService } from '@/services/provider_users_service'
import { useAuth } from '@/hooks/useAuth'
import { useUserRoles } from '@/hooks/useUserRoles'
import { toast } from 'sonner'

const formSchema = z.object({
  account: z.string().min(1, { message: '請輸入使用者帳號' }),
  user_name: z.string().min(1, { message: '請輸入使用者名稱' }),
  email: emailSchema(),
  user_role_id: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ProviderUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  providerId: number | null
  onSuccess?: () => void
}

export function ProviderUserDialog({
  open,
  onOpenChange,
  providerId,
  onSuccess,
}: ProviderUserDialogProps) {
  const { token } = useAuth()

  // Fetch provider-level roles for this provider
  const { userRoles, isLoading: isLoadingRoles } = useUserRoles({
    pageSize: 100,
    scopeType: 'provider',
    providerId: providerId ?? undefined,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: '',
      user_name: '',
      email: '',
      user_role_id: '',
    },
  })

  const onSubmit = async (values: FormValues) => {
    if (!token || !providerId) return

    const result = await providerUsersService.createNewUserAndAddToProvider(
      {
        provider_id: providerId,
        account: values.account,
        user_name: values.user_name,
        email: values.email,
        user_role_id: values.user_role_id ? parseInt(values.user_role_id, 10) : undefined,
      },
      token,
    )

    if (result.success) {
      toast.success('新增使用者與 Provider 關聯成功')
      form.reset()
      onOpenChange(false)
      onSuccess?.()
    } else {
      toast.error(result.error?.message || '帳號可能已存在')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>新增 Provider 使用者</DialogTitle>
          <DialogDescription>新增使用者至 Provider</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="account"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者帳號</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者帳號" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>使用者名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="請輸入使用者名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="example@domain.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="user_role_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色 (選填)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoadingRoles ? '載入中...' : '請選擇角色'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {userRoles.map((role) => (
                        <SelectItem key={role.user_role_id} value={role.user_role_id.toString()}>
                          {role.role_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit">新增</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
